import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Trash2, Plus, Minus, Zap } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export const CartDrawer = () => {
    const navigate = useNavigate();
    const { items, drawerOpen, setDrawerOpen, updateQty, removeItem, total } =
        useCart();
    const { user } = useAuth();
    const [expressBusy, setExpressBusy] = useState(false);

    /**
     * Express Checkout — skips the cart-review page and fires Stripe Checkout
     * directly. Stripe's hosted page automatically surfaces Apple Pay / Google
     * Pay buttons when the device supports them; users with neither still see
     * the standard card form, so this is a safe upgrade for everyone.
     */
    const expressCheckout = async () => {
        if (items.length === 0 || expressBusy) return;
        setExpressBusy(true);
        try {
            const payload = {
                items: items.map((it) => ({
                    product_id: it.product_id,
                    size: it.size,
                    quantity: it.quantity,
                })),
                origin_url: window.location.origin,
                email: user?.email || null,
            };
            const r = await api.post("/checkout", payload);
            if (r.data?.url) {
                window.location.assign(r.data.url);
            } else {
                throw new Error("No checkout URL returned");
            }
        } catch (err) {
            console.error("Express checkout failed:", err);
            toast.error(err?.response?.data?.detail || "Express checkout unavailable — try the regular checkout button.");
            setExpressBusy(false);
        }
    };

    if (!drawerOpen) return null;

    return (
        <div
            data-testid="cart-drawer"
            className="fixed inset-0 z-50 flex justify-end"
        >
            <div
                className="absolute inset-0 bg-black/60"
                onClick={() => setDrawerOpen(false)}
            />
            <aside className="relative bg-[#FDFBF7] w-full max-w-md h-full border-l-4 border-black flex flex-col">
                <div className="flex items-center justify-between p-5 border-b-2 border-black">
                    <h3 className="font-display text-2xl font-black uppercase tracking-tight">
                        Your Bag
                    </h3>
                    <button
                        onClick={() => setDrawerOpen(false)}
                        data-testid="close-cart-drawer"
                        className="border-2 border-black p-2 bg-white shadow-brutal-sm btn-press"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {items.length === 0 && (
                        <p
                            data-testid="empty-cart-msg"
                            className="text-sm text-black/70 font-mono"
                        >
                            Empty bag. Go fill it up.
                        </p>
                    )}
                    {items.map((it) => (
                        <div
                            key={`${it.product_id}-${it.size}`}
                            className="border-2 border-black bg-white p-3 flex gap-3"
                            data-testid={`cart-item-${it.product_id}`}
                        >
                            {it.image_url && (
                                <img
                                    src={it.image_url}
                                    alt={it.name}
                                    className="w-20 h-20 object-cover border-2 border-black"
                                />
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="flex justify-between gap-2">
                                    <p className="font-bold text-sm truncate">{it.name}</p>
                                    <button
                                        onClick={() => removeItem(it.product_id, it.size)}
                                        data-testid={`remove-${it.product_id}`}
                                        className="text-black/60 hover:text-[#FF00FF]"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                <p className="font-mono text-xs uppercase">size · {it.size}{it.store_name ? ` · ${it.store_name}` : ""}</p>
                                <div className="flex items-center justify-between mt-2">
                                    <div className="inline-flex items-center border-2 border-black">
                                        <button
                                            onClick={() =>
                                                updateQty(it.product_id, it.size, it.quantity - 1)
                                            }
                                            className="px-2 py-1"
                                            data-testid={`dec-${it.product_id}`}
                                        >
                                            <Minus className="w-3 h-3" />
                                        </button>
                                        <span className="px-3 font-mono text-sm">
                                            {it.quantity}
                                        </span>
                                        <button
                                            onClick={() =>
                                                updateQty(it.product_id, it.size, it.quantity + 1)
                                            }
                                            className="px-2 py-1"
                                            data-testid={`inc-${it.product_id}`}
                                        >
                                            <Plus className="w-3 h-3" />
                                        </button>
                                    </div>
                                    <p className="font-bold">
                                        ${(it.price * it.quantity).toFixed(2)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="border-t-2 border-black p-5 space-y-3">
                    <div className="flex justify-between items-baseline">
                        <span className="font-mono text-sm uppercase tracking-widest">
                            Subtotal
                        </span>
                        <span
                            data-testid="cart-subtotal"
                            className="font-display text-3xl font-black"
                        >
                            ${total.toFixed(2)}
                        </span>
                    </div>
                    <button
                        disabled={items.length === 0 || expressBusy}
                        onClick={expressCheckout}
                        data-testid="express-checkout-btn"
                        aria-label="Express checkout — Apple Pay, Google Pay, or card"
                        className="w-full bg-black text-[#CCFF00] border-2 border-black font-display font-black uppercase tracking-widest py-3 shadow-brutal btn-press disabled:opacity-40 inline-flex items-center justify-center gap-2"
                    >
                        <Zap className="w-4 h-4" />
                        {expressBusy ? "Connecting…" : `Express checkout · $${total.toFixed(2)}`}
                    </button>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-black/50 text-center">
                        Apple Pay · Google Pay · Card — auto-detected
                    </p>
                    <button
                        disabled={items.length === 0}
                        onClick={() => {
                            setDrawerOpen(false);
                            navigate("/cart");
                        }}
                        data-testid="view-cart-btn"
                        className="w-full bg-white border-2 border-black font-bold uppercase tracking-widest py-3 shadow-brutal-sm btn-press disabled:opacity-40"
                    >
                        Review bag
                    </button>
                </div>
            </aside>
        </div>
    );
};
